import { catalogFields } from "./formField/CatalogFormField";
import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "../../../components/AppToast";
import { getItemList } from "../../../services/skuItem/skuItem";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, InputAdornment,
  CircularProgress, Typography, IconButton, Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const CreateCatalog = ({ initialValues, onCancel, onSubmit, viewOnly, isEdit }) => {
  const [formData, setFormData] = useState({
    catalogChannelId: "",
    channelSkuId: "",
    itemName: "",
    itemCode: "",
    brand: "",
    color: "",
    size: "",
    category: "",
    subCategory: "",
    shipmentMethod: "",
    shippingSpeed: "",
  });

  const [skuPickerOpen, setSkuPickerOpen] = useState(false);
  const [skuItems, setSkuItems] = useState([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuSearch, setSkuSearch] = useState("");

  useEffect(() => {
    if ((viewOnly || isEdit) && initialValues) {
      setFormData({
        catalogChannelId: initialValues.catalogChannelId || "",
        channelSkuId: initialValues.channelSkuId || "",
        itemName: initialValues.itemName || "",
        itemCode: initialValues.itemCode || "",
        brand: initialValues.brand || "",
        color: initialValues.color || "",
        size: initialValues.size || "",
        category: initialValues.category || "",
        subCategory: initialValues.subCategory || "",
        shipmentMethod: initialValues.shipmentMethod || "",
        shippingSpeed: initialValues.shippingSpeed || "",
      });
    }
  }, [viewOnly, isEdit, initialValues]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchSkuItems = useCallback(async () => {
    setSkuLoading(true);
    try {
      const res = await getItemList(0, 50);
      if (res && Object.keys(res).length !== 0) {
        const list = res.list.map((item) => ({
          id: item._id,
          itemCode: item.itemCode,
          itemName: item.itemName,
          brand: item.brand || "",
          color: item.attributes?.Color || item.attributes?.color || "",
          size: item.attributes?.Size || item.attributes?.size || "",
          subCategory: item.subCategory || "",
          category: item.category || "",
        }));
        setSkuItems(list);
      } else {
        setSkuItems([]);
      }
    } catch {
      setSkuItems([]);
    } finally {
      setSkuLoading(false);
    }
  }, []);

  const openSkuPicker = () => {
    setSkuPickerOpen(true);
    setSkuSearch("");
    fetchSkuItems();
  };

  const handleSkuSelect = (sku) => {
    setFormData((prev) => ({
      ...prev,
      itemName: sku.itemName,
      itemCode: sku.itemCode,
      brand: sku.brand,
      color: sku.color,
      size: sku.size,
      subCategory: sku.subCategory,
      category: sku.category,
    }));
    setSkuPickerOpen(false);
  };

  const filteredSkuItems = useMemo(() => {
    if (!skuSearch.trim()) return skuItems;
    const q = skuSearch.toLowerCase();
    return skuItems.filter(
      (s) =>
        s.itemName?.toLowerCase().includes(q) ||
        s.itemCode?.toLowerCase().includes(q) ||
        s.brand?.toLowerCase().includes(q) ||
        s.category?.toLowerCase().includes(q)
    );
  }, [skuItems, skuSearch]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const finalData = { ...formData };
      if (isEdit) {
        await onSubmit(finalData, initialValues._id);
      } else {
        await onSubmit(finalData);
      }
    } catch (err) {
      if (err?.data?.message) {
        toast.error(err.data.data);
      } else {
        toast.error("Error saving catalog item");
      }
    }
  };

  return (
    <>
      <form>
        <div className="d-flex flex-wrap gap-3">
          {catalogFields.map((field) => (
            <div
              key={field.name}
              style={{ flex: "0 0 23%" }}
              className="mb-3"
            >
              <label className="form-label">
                {field.label}
                {field.required && <span className="text-danger"> *</span>}
              </label>

              {field.type === "skuPicker" ? (
                <div>
                  <div className="d-flex gap-2 align-items-center">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.itemName || ""}
                      readOnly
                      placeholder="Click 'Pick Item' to select"
                    />
                    {!viewOnly && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={openSkuPicker}
                      >
                        Pick Item
                      </button>
                    )}
                  </div>
                  {formData.itemCode && (
                    <small className="text-primary">
                      Item Code: {formData.itemCode}
                    </small>
                  )}
                </div>
              ) : field.type === "select" ? (
                <select
                  className="form-select"
                  value={formData[field.name] || ""}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  disabled={viewOnly}
                >
                  <option value="">Select</option>
                  {field.options?.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={formData[field.name] || ""}
                  required={field.required}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  readOnly={viewOnly}
                />
              )}
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-center gap-2 mt-3">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Back
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            style={{ display: !viewOnly && !isEdit ? "inline-flex" : "none" }}
          >
            Save
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            style={{ display: isEdit && !viewOnly ? "inline-flex" : "none" }}
          >
            Update
          </button>
        </div>
      </form>

      {/* SKU Picker Dialog */}
      <Dialog open={skuPickerOpen} onClose={() => setSkuPickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Select Item from SKU Management</Typography>
          <IconButton size="small" onClick={() => setSkuPickerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth size="small"
            placeholder="Search by item name, code, brand or category..."
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {skuLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredSkuItems.length === 0 ? (
            <Typography color="text.secondary" align="center" py={4}>
              No SKU items found
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Sub Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Color</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSkuItems.map((sku) => (
                    <TableRow key={sku.id} hover sx={{ cursor: "pointer" }} onClick={() => handleSkuSelect(sku)}>
                      <TableCell>{sku.itemCode}</TableCell>
                      <TableCell>{sku.itemName}</TableCell>
                      <TableCell>{sku.brand}</TableCell>
                      <TableCell>{sku.category}</TableCell>
                      <TableCell>{sku.subCategory}</TableCell>
                      <TableCell>{sku.color}</TableCell>
                      <TableCell>{sku.size}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small" variant="outlined"
                          startIcon={<CheckCircleIcon />}
                          onClick={(e) => { e.stopPropagation(); handleSkuSelect(sku); }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkuPickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateCatalog;
